// packages/components/calendar/CalendarDay.js
import React from "react";

/**
 * Helper function to concatenate class names
 */
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Calendar day cell component
 *
 * @param {Object} props
 * @param {Object} props.day - Day data
 * @param {function} props.onSelect - Handler for day selection
 * @param {function} [props.renderDay] - Custom day renderer
 * @param {boolean} props.isDesktop - Whether the component is rendered on desktop
 */
const CalendarDay = ({ day, onSelect, renderDay, isDesktop }) => {
  if (renderDay) {
    return renderDay({ day, onSelect, isDesktop });
  }

  const dayNumber = day.date.split("-").pop().replace(/^0/, "");

  if (isDesktop) {
    return (
      <div
        className={classNames(
          day.isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-500",
          "relative px-3 py-2"
        )}
      >
        <time
          dateTime={day.date}
          className={
            day.isToday
              ? "flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white"
              : undefined
          }
        >
          {dayNumber}
        </time>
        {day.events && day.events.length > 0 && (
          <ol className="mt-2">
            {day.events.slice(0, 2).map((event) => (
              <li key={event.id}>
                <a href={event.href} className="group flex">
                  <p className="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600">
                    {event.name}
                  </p>
                  <time
                    dateTime={event.datetime}
                    className="ml-3 hidden flex-none text-gray-500 group-hover:text-indigo-600 xl:block"
                  >
                    {event.time}
                  </time>
                </a>
              </li>
            ))}
            {day.events.length > 2 && (
              <li className="text-gray-500">+ {day.events.length - 2} more</li>
            )}
          </ol>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={classNames(
        day.isCurrentMonth ? "bg-white" : "bg-gray-50",
        (day.isSelected || day.isToday) && "font-semibold",
        day.isSelected && "text-white",
        !day.isSelected && day.isToday && "text-indigo-600",
        !day.isSelected &&
          day.isCurrentMonth &&
          !day.isToday &&
          "text-gray-900",
        !day.isSelected &&
          !day.isCurrentMonth &&
          !day.isToday &&
          "text-gray-500",
        "flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10"
      )}
    >
      <time
        dateTime={day.date}
        className={classNames(
          day.isSelected &&
            "flex h-6 w-6 items-center justify-center rounded-full",
          day.isSelected && day.isToday && "bg-indigo-600",
          day.isSelected && !day.isToday && "bg-gray-900",
          "ml-auto"
        )}
      >
        {dayNumber}
      </time>
      {day.events && day.events.length > 0 && (
        <>
          <span className="sr-only">{day.events.length} events</span>
          <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
            {day.events.map((event) => (
              <span
                key={event.id}
                className="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-400"
              />
            ))}
          </span>
        </>
      )}
    </button>
  );
};

export default CalendarDay;
